import { Input } from '@/components/ui/input';
import { useSetTravelEsimNumber } from '@/hooks/useTravelEsimOrderActions';
import { Check, Loader2, Pencil } from 'lucide-react';
import { useState } from 'react';

interface TravelEsimNumberFieldProps {
  orderId: string;
  itemId: string;
  esimNumber: string | null;
}

/**
 * Admin entry of the provider's eSIM number on an order line. Recorded at
 * completion so a later trip can recharge that eSIM instead of ordering a
 * new one. The number itself is shown by the line renderer; once saved,
 * only a "modify" link remains here.
 */
export function TravelEsimNumberField({
  orderId,
  itemId,
  esimNumber,
}: TravelEsimNumberFieldProps) {
  const [editing, setEditing] = useState(esimNumber === null);
  const [value, setValue] = useState(esimNumber ?? '');
  const setNumber = useSetTravelEsimNumber(orderId);

  const save = () => {
    const trimmed = value.trim();
    if (trimmed.length < 5) return;
    setNumber.mutate(
      { itemId, esimNumber: trimmed },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (!editing && esimNumber) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 cursor-pointer"
      >
        <Pencil className="h-3 w-3" />
        Modifier le numéro eSIM
      </button>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        save();
      }}
      className="mt-2 flex items-center gap-2"
    >
      <Input
        value={value}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Numéro eSIM (ICCID)"
        className="h-8 w-56 rounded-lg font-mono text-xs"
        disabled={setNumber.isPending}
      />
      <button
        type="submit"
        disabled={setNumber.isPending || value.trim().length < 5}
        className="inline-flex h-8 items-center gap-1 rounded-lg bg-slate-50 px-3 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50 cursor-pointer"
      >
        {setNumber.isPending ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <Check className="h-3 w-3" />
        )}
        Enregistrer
      </button>
    </form>
  );
}
